import * as faceapi from '@vladmandic/face-api';

const MODEL_URL = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.15/model/';
const WASM_URL = 'https://cdn.jsdelivr.net/npm/@tensorflow/tfjs-backend-wasm@4.22.0/dist/';
let loading = null;

const pickBackend = async () => {
  const tf = faceapi.tf;
  for (const b of ['webgl', 'wasm', 'cpu']) {
    try {
      if (b === 'wasm' && tf.setWasmPaths) tf.setWasmPaths(WASM_URL);
      if (await tf.setBackend(b)) { await tf.ready(); return b; }
    } catch (e) { /* try next */ }
  }
  return tf.getBackend();
};

export const loadFaceModels = () => {
  if (!loading) {
    loading = pickBackend().then(() => Promise.all([
      faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
      faceapi.nets.faceLandmark68TinyNet.loadFromUri(MODEL_URL),
      faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
    ])).catch((e) => { loading = null; throw e; });
  }
  return loading;
};

/** Returns Float32Array descriptor (128) of the single largest face, or null. */
export const describeFace = async (videoOrImg) => {
  const det = await faceapi.detectSingleFace(videoOrImg, new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.5 }))
    .withFaceLandmarks(true).withFaceDescriptor();
  return det ? { descriptor: Array.from(det.descriptor), box: det.detection.box, score: det.detection.score } : null;
};

export const faceDistance = (a, b) => faceapi.euclideanDistance(Float32Array.from(a), Float32Array.from(b));

/** Best match among employees with face_descriptor. Returns {employee, distance} or null. */
export const matchFace = (descriptor, employees, threshold = 0.5) => {
  let best = null;
  for (const e of employees) {
    if (!e.face_descriptor) continue;
    const d = faceDistance(descriptor, e.face_descriptor);
    if (d <= threshold && (!best || d < best.distance)) best = { employee: e, distance: d };
  }
  return best;
};

export const snapshot = (video, w = 480) => {
  const c = document.createElement('canvas');
  const ratio = video.videoHeight / video.videoWidth || 0.75;
  c.width = w; c.height = Math.round(w * ratio);
  c.getContext('2d').drawImage(video, 0, 0, c.width, c.height);
  return c.toDataURL('image/jpeg', 0.7);
};

const toPos = (p) => ({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy ?? 9999, ts: p.timestamp || Date.now() });

/** Haversine distance in metres. */
export const distanceM = (a, b) => {
  const r = 6371000; const toRad = (x) => (x * Math.PI) / 180;
  const p1 = toRad(a.lat); const p2 = toRad(b.lat);
  const h = Math.sin((p2 - p1) / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(toRad(b.lng - a.lng) / 2) ** 2;
  return 2 * r * Math.asin(Math.sqrt(h));
};

/**
 * Continuous GPS watch that keeps the BEST (most accurate, fresh) fix instead of the first coarse one.
 * onUpdate(best, {samples}) fires whenever a better/fresh sample arrives. Returns stop().
 */
export const watchBestPosition = (onUpdate, onError, { goodAccuracy = 20, staleMs = 20000 } = {}) => {
  if (!navigator.geolocation) { onError?.(new Error('GPS tidak didukung di perangkat ini')); return () => {}; }
  let best = null; let samples = 0;
  const id = navigator.geolocation.watchPosition((p) => {
    const pos = toPos(p); samples += 1;
    const stale = best && pos.ts - best.ts > staleMs;
    if (!best || stale || pos.accuracy <= best.accuracy || (pos.accuracy <= goodAccuracy && pos.accuracy <= best.accuracy * 1.5)) best = pos;
    onUpdate(best, { samples, latest: pos });
  }, (e) => onError?.(e), { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 });
  return () => navigator.geolocation.clearWatch(id);
};

/** One-shot: wait up to `maxMs` for a fix with accuracy ≤ goodAccuracy, else return the best seen. */
export const getPosition = ({ maxMs = 12000, goodAccuracy = 20 } = {}) => new Promise((resolve, reject) => {
  let best = null; let stop = () => {};
  const timer = setTimeout(() => { stop(); best ? resolve(best) : reject(new Error('GPS timeout')); }, maxMs);
  stop = watchBestPosition((b) => { best = b; if (b.accuracy <= goodAccuracy) { clearTimeout(timer); stop(); resolve(b); } }, (e) => { clearTimeout(timer); stop(); best ? resolve(best) : reject(e); }, { goodAccuracy });
});
